import React, { useEffect, useMemo, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ImageUploadField from "@/components/shared/ImageUploadField";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

const DISCOUNT_TYPES = ["PERCENTAGE", "FIXED_PACKAGE"];
const HARD_FIELDS = new Set(["discount_type", "discount_value", "original_price"]);

const EMPTY = {
    title_en: "", title_ar: "",
    description_en: "", description_ar: "",
    terms_en: "", terms_ar: "",
    image_url: null,
    discount_type: "PERCENTAGE",
    discount_value: 20,
    original_price: "",
    start_date: "",
    end_date: "",
    location_ids: [],
    staff_ids: [],
    category_ids: [],
};

/**
 * Shared bilingual offer create/edit dialog.
 * Consumers pass `save({ id, payload })` returning Promise<savedOffer>.
 * `locations`, `staff`, `categories` are arrays for the multi-select panes.
 */
export default function OfferFormDialog({
    open, onOpenChange, initial, save, onSaved,
    locations = [], staff = [], categories = [],
}) {
    const { t, isRTL, lang } = useI18n();
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        if (initial?.id) {
            setForm({
                ...EMPTY, ...initial,
                image_url: initial.image_url || null,
                original_price: initial.original_price ?? "",
                location_ids: initial.location_ids || [],
                staff_ids: initial.staff_ids || [],
                category_ids: initial.category_ids || [],
            });
        } else {
            setForm(EMPTY);
        }
        setError("");
    }, [open, initial]);

    const update = (patch) => setForm((p) => ({ ...p, ...patch }));

    const toggleId = (key, id) => {
        setForm((p) => {
            const has = (p[key] || []).includes(id);
            return { ...p, [key]: has ? p[key].filter((x) => x !== id) : [...(p[key] || []), id] };
        });
    };

    const isApprovedEdit = initial?.status === "APPROVED";
    const willRequireReapproval = useMemo(() => {
        if (!isApprovedEdit) return false;
        for (const f of HARD_FIELDS) {
            const a = form[f] === "" ? null : form[f];
            const b = initial?.[f] ?? null;
            if (String(a ?? "") !== String(b ?? "")) return true;
        }
        return false;
    }, [form, initial, isApprovedEdit]);

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setSaving(true);
        try {
            const payload = {
                ...form,
                title_en: form.title_en.trim(),
                title_ar: form.title_ar.trim(),
                description_en: form.description_en?.trim() || null,
                description_ar: form.description_ar?.trim() || null,
                terms_en: form.terms_en?.trim() || null,
                terms_ar: form.terms_ar?.trim() || null,
                image_url: form.image_url || null,
                discount_value: Number(form.discount_value),
                original_price: form.original_price === "" || form.original_price === null
                    ? null
                    : Number(form.original_price),
            };
            const saved = await save({ id: initial?.id || null, payload });
            toast.success(t(initial?.id ? "offers.toasts.updated" : "offers.toasts.created"));
            onSaved?.(saved);
            onOpenChange(false);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl"
                data-testid="offer-dialog"
                dir={isRTL ? "rtl" : "ltr"}
            >
                <DialogHeader>
                    <DialogTitle>{t(initial?.id ? "offers.editTitle" : "offers.createTitle")}</DialogTitle>
                    <DialogDescription>{t("offers.dialogDescription")}</DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-4 mt-2">
                    <div>
                        <Label>{t("offers.fields.image")}</Label>
                        <p className="text-xs text-muted-foreground mt-1 mb-2">{t("offers.fields.imageHint")}</p>
                        <ImageUploadField
                            value={form.image_url}
                            onChange={(v) => update({ image_url: v })}
                            shape="wide"
                            maxDim={1200}
                            testIdPrefix="offer-image"
                            label={t("offers.fields.imageLabel")}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>{t("offers.fields.titleEn")}</Label>
                            <Input dir="ltr" required value={form.title_en} onChange={(e) => update({ title_en: e.target.value })} className="mt-1.5" data-testid="offer-title-en" />
                        </div>
                        <div>
                            <Label>{t("offers.fields.titleAr")}</Label>
                            <Input dir="rtl" lang="ar" required value={form.title_ar} onChange={(e) => update({ title_ar: e.target.value })} className="mt-1.5 font-arabic" data-testid="offer-title-ar" />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>{t("offers.fields.descriptionEn")}</Label>
                            <Textarea rows={2} dir="ltr" value={form.description_en || ""} onChange={(e) => update({ description_en: e.target.value })} className="mt-1.5" data-testid="offer-desc-en" />
                        </div>
                        <div>
                            <Label>{t("offers.fields.descriptionAr")}</Label>
                            <Textarea rows={2} dir="rtl" lang="ar" value={form.description_ar || ""} onChange={(e) => update({ description_ar: e.target.value })} className="mt-1.5 font-arabic" data-testid="offer-desc-ar" />
                        </div>
                    </div>

                    {/* Discount block — these fields are commercially sensitive */}
                    <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
                        <div>
                            <Label className="text-base">{t("offers.fields.discount")}</Label>
                            <p className="text-xs text-muted-foreground mt-0.5">{t("offers.fields.discountHint")}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <Label>{t("offers.fields.discountType")}</Label>
                                <Select value={form.discount_type} onValueChange={(v) => update({ discount_type: v })}>
                                    <SelectTrigger className="mt-1.5" data-testid="offer-discount-type">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {DISCOUNT_TYPES.map((typ) => (
                                            <SelectItem key={typ} value={typ}>{t(`offers.discountType.${typ}`)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>
                                    {form.discount_type === "PERCENTAGE"
                                        ? t("offers.fields.percentValue")
                                        : t("offers.fields.packagePrice")}
                                </Label>
                                <Input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    required
                                    value={form.discount_value}
                                    onChange={(e) => update({ discount_value: e.target.value })}
                                    className="mt-1.5"
                                    data-testid="offer-discount-value"
                                />
                            </div>
                            <div>
                                <Label>
                                    {form.discount_type === "PERCENTAGE"
                                        ? t("offers.fields.averagePriceLabel")
                                        : t("offers.fields.originalPrice")}
                                </Label>
                                <Input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    required
                                    placeholder={t("offers.fields.originalPricePlaceholder")}
                                    value={form.original_price}
                                    onChange={(e) => update({ original_price: e.target.value })}
                                    className="mt-1.5"
                                    data-testid="offer-original-price"
                                />
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground -mt-1">
                            {form.discount_type === "PERCENTAGE"
                                ? t("offers.fields.originalPriceHintPercentage")
                                : t("offers.fields.originalPriceHintPackage")}
                        </p>
                        {willRequireReapproval && (
                            <div className="rounded-lg border border-warning/40 bg-warning/5 px-3 py-2 text-xs" data-testid="offer-reapproval-warning">
                                {t("offers.reapprovalWarning")}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>{t("offers.fields.startDate")}</Label>
                            <Input type="date" required value={form.start_date} onChange={(e) => update({ start_date: e.target.value })} className="mt-1.5" data-testid="offer-start-date" />
                        </div>
                        <div>
                            <Label>{t("offers.fields.endDate")}</Label>
                            <Input type="date" required value={form.end_date} onChange={(e) => update({ end_date: e.target.value })} className="mt-1.5" data-testid="offer-end-date" />
                        </div>
                    </div>

                    {/* Scope */}
                    <ScopePicker
                        title={t("offers.fields.locations")}
                        emptyHint={t("offers.fields.locationsAll")}
                        items={locations}
                        selected={form.location_ids}
                        onToggle={(id) => toggleId("location_ids", id)}
                        labelKey={lang === "ar" ? "name_ar" : "name_en"}
                        testidPrefix="offer-location"
                    />
                    <ScopePicker
                        title={t("offers.fields.staff")}
                        emptyHint={t("offers.fields.staffAll")}
                        items={staff}
                        selected={form.staff_ids}
                        onToggle={(id) => toggleId("staff_ids", id)}
                        labelKey={lang === "ar" ? "full_name_ar" : "full_name_en"}
                        testidPrefix="offer-staff"
                    />
                    <ScopePicker
                        title={t("offers.fields.categories")}
                        emptyHint={t("offers.fields.categoriesAll")}
                        items={categories}
                        selected={form.category_ids}
                        onToggle={(id) => toggleId("category_ids", id)}
                        labelKey={lang === "ar" ? "name_ar" : "name_en"}
                        testidPrefix="offer-category"
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <Label>{t("offers.fields.termsEn")}</Label>
                            <Textarea rows={2} dir="ltr" value={form.terms_en || ""} onChange={(e) => update({ terms_en: e.target.value })} className="mt-1.5" data-testid="offer-terms-en" />
                        </div>
                        <div>
                            <Label>{t("offers.fields.termsAr")}</Label>
                            <Textarea rows={2} dir="rtl" lang="ar" value={form.terms_ar || ""} onChange={(e) => update({ terms_ar: e.target.value })} className="mt-1.5 font-arabic" data-testid="offer-terms-ar" />
                        </div>
                    </div>

                    {error && <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="offer-form-error">{error}</div>}

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="offer-cancel">{t("common.cancel")}</Button>
                        <Button type="submit" disabled={saving} data-testid="offer-save">
                            {saving && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                            {t("common.save")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ScopePicker({ title, emptyHint, items, selected, onToggle, labelKey, testidPrefix }) {
    if (!items || items.length === 0) return null;
    return (
        <div>
            <Label>{title}</Label>
            <p className="text-xs text-muted-foreground mt-1">{emptyHint}</p>
            <div className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-border p-2 space-y-1">
                {items.map((item) => {
                    const checked = selected.includes(item.id);
                    return (
                        <label
                            key={item.id}
                            className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer text-sm ${checked ? "bg-secondary" : "hover:bg-muted/40"}`}
                            data-testid={`${testidPrefix}-${item.id}`}
                        >
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => onToggle(item.id)}
                                className="h-4 w-4 accent-primary"
                            />
                            <span className="font-medium truncate">{item[labelKey] || item.name_en || item.full_name_en}</span>
                        </label>
                    );
                })}
            </div>
            {selected.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {selected.map((id) => {
                        const item = items.find((x) => x.id === id);
                        if (!item) return null;
                        return (
                            <span key={id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs">
                                {item[labelKey] || item.name_en || item.full_name_en}
                                <button type="button" onClick={() => onToggle(id)} className="hover:bg-primary/20 rounded-full p-0.5" aria-label="remove">
                                    <X className="h-3 w-3" />
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
